// Allocation Arena API client — the read-only PUBLIC REST service at
// https://api-arena.santiment.net (no auth, no stage environment). Spec:
// scripts/swagger-snapshot/arena.json (see the swagger-snapshot gate).
//
// Query-side quirks: `filter` is a JSON object in a query param —
// case-sensitive, expects EIP-55 checksummed addresses; OR-of-equalities on
// /v1/issuers works. No request asks for more than 500 rows per page — the
// backend capped `limit` at 500 once and may again (pinned by the
// swagger-snapshot gate).

import { z } from 'zod';
import {
  queryOptions,
  skipToken,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { absolutizeAvatarUrl } from '@/modules/avatar';
import { fetchJson, pingOk } from '@/modules/fetch-core';
import {
  parseRowsWithDegrade,
  type MalformedRow,
} from '@/modules/malformed-row';
import { safeFloat } from '@/modules/safe-float';
import {
  weiString,
  decimalSanString,
  pct,
  ratio01,
  decimalRatioString,
} from '@/modules/brands';
import {
  AddressSchema,
  type ChecksummedAddress,
} from '@/modules/address-schema';
import { queryKeys } from '@/modules/query-keys';
import { eliminationOf } from '@/modules/arena-elimination';

const ARENA_API_BASE = 'https://api-arena.santiment.net';

// /v1/issuers row — exported (with IssuersPageSchema) for composing further
// issuer reads through arenaFetch (custom filters/sorts) without redeclaring
// the DTO. Bare wallets get issuer rows too (e.g. a staker that never
// registered a profile) — those ship username/avatar null.
export const IssuerSchema = z.looseObject({
  id: z.string(),
  address: AddressSchema.nullable(), // null = walletless SSO account
  username: z.string().nullable(), // null = bare wallet
  avatar: z.string().nullable(), // may be a relative path — consumers absolutize
  active_stake_on_user: decimalSanString,
  total_predictions: safeFloat,
  position: safeFloat.nullable(),
  accuracy: ratio01.nullable(),
  first_prediction_at: z.string().nullable(),
  last_prediction_at: z.string().nullable(),
  // Which bucket a prediction lands in (e.g. zero-performance rows) is
  // backend-owned — the positive/negative split is rendered as-is; don't
  // build logic on it.
  avg_performance: decimalRatioString.nullable(),
  avg_performance_positive: decimalRatioString.nullable(),
  avg_performance_negative: decimalRatioString.nullable(),
  avg_prediction_duration: safeFloat.nullable(), // seconds
  // Always present ([] when none). rank = "tier reached", ONE row per medal
  // type — the backend upgrades a medal in place rather than adding rows;
  // consumers look a medal up by type and derive the displayed tier from
  // rank, so a rows-per-tier model breaks them.
  medals: z.array(
    z.looseObject({
      type: z.string(),
      rank: safeFloat,
    }),
  ),
});
export type Issuer = z.output<typeof IssuerSchema>;

const HlEquityCurveSchema = z.looseObject({
  time: z.array(z.number()),
  pnl: z.array(z.number()),
  value: z.array(z.number()),
});

// Backend-computed trading metrics — consumers only format. sharpe/sortino
// are unitless. null = uncomputed on a sparse account — render as absent,
// never as 0.
const MetricsSchema = z.looseObject({
  sharpe: safeFloat.nullable(),
  sortino: safeFloat.nullable(),
  mdd: ratio01.nullable(),
  // Percent of net deposited capital (deposits − withdrawals). The wire's
  // cumulative_pnl_pct — their sum, the equity curve's last point — stays
  // undeclared: total return is read from the curve alone.
  realized_pnl_pct: pct.nullable(),
  unrealized_pnl_pct: pct.nullable(),
  // Stamp of the account snapshot the metrics were computed from — the
  // data-freshness signal consumers render as "last updated". null = no
  // snapshot yet — render no freshness claim, never a fabricated one.
  last_snapshot_at: z.string().nullable(),
});

const positionBase = {
  id: z.string(),
  asset: z.string(), // bare ticker (e.g. "BTC") — no slug/logo; resolve those elsewhere
  side: z.enum(['long', 'short']),
  size: safeFloat,
  entry_price: safeFloat,
  realized_pnl: safeFloat,
  opened_at: z.string(),
  closed_at: z.string().nullable(),
};
// Hyperliquid position row. All money fields USD. Only the
// unrealized_pnl⇄is_open correlation is encoded: unrealized_pnl (mark price
// at fetch time) is null on closed rows; it stays NULLABLE on open rows too
// — a live open row shipped null; the looseness is deliberate, don't
// tighten. closed_at carries NO is_open correlation claim — the spec
// documents none (live data happens to correlate; don't rely on it).
const PositionSchema = z.discriminatedUnion('is_open', [
  z.looseObject({
    ...positionBase,
    is_open: z.literal(true),
    unrealized_pnl: safeFloat.nullable(),
  }),
  z.looseObject({
    ...positionBase,
    is_open: z.literal(false),
    unrealized_pnl: z.null(),
  }),
]);
export type Position = z.output<typeof PositionSchema>;

const TradeSchema = z.looseObject({
  id: z.string(),
  asset: z.string(),
  dir: z.string(), // e.g. "Open Long" / "Close Long" — drives buy/sell marker placement
  size: safeFloat,
  price: safeFloat,
  realized_pnl: safeFloat,
  filled_at: z.string(),
});
export type Trade = z.output<typeof TradeSchema>;

const MarketSchema = z.looseObject({
  asset: z.string(),
  count: safeFloat,
});
const MarketsSchema = z.array(MarketSchema);

const WeekSchema = z.looseObject({
  reward_basis: safeFloat, // USD
  is_final: z.boolean(),
});
const WeeklySchema = z.array(WeekSchema);

type Page<T> = { data: T[]; total: number; limit: number; offset: number };

const pageSchema = <S extends z.ZodType>(item: S) =>
  z.looseObject({
    data: z.array(item),
    total: safeFloat,
    limit: safeFloat,
    offset: safeFloat,
  });

export const IssuersPageSchema = pageSchema(IssuerSchema);
// Positions parse ROW-LEVEL (see getPositionsPage) — the envelope keeps rows
// unknown so one bad row degrades alone instead of failing the page to raw.
const PositionsEnvelopeSchema = pageSchema(z.unknown());
const TradesPageSchema = pageSchema(TradeSchema);

// /v1/allocations row — one Hyperliquid account traded over one bounded
// window. Every /hyperliquid/* data endpoint is keyed by its id, never by
// the issuer. At most one `active` allocation per issuer. `pending` is
// registered but not yet trading (only deposits synced). `soft_stopped`
// is still live — the executor trades no new signals (drawdown breach)
// while the open positions wind down; `stopped` is closed and final.
// Holding a LIVE allocation is what makes an issuer a funded provider: once
// it is `stopped` the issuer is funded no longer and only the trading
// history remains. Whether a stop is an elimination, and why, is read off
// the row by @/modules/arena-elimination. The issuer row's own allocation_amount/allocation_date
// columns are not maintained for new allocations (two of four allocated
// issuers carried null in both, verified live 2026-09-28), so nothing
// reads them.
const AllocationSchema = z.looseObject({
  id: z.string(),
  issuer_id: z.string(),
  // The issuer row's wallet, the same stored string — the key that names
  // funded providers in /v1/issuers address filters.
  issuer_address: AddressSchema,
  status: z.enum(['active', 'stopped', 'soft_stopped', 'pending']),
  // Why trading stopped — written at the soft stop, kept as is once the
  // window closes, null while active or pending. The spec's closed set,
  // pinned by the swagger-snapshot gate: `other` is a reason the backend's
  // list does not know yet (it still eliminates, with no reason clause).
  // unread-field-allow: consumed via @/modules/arena-elimination — whole Allocation rows pass into its structurally-typed param
  stop_reason: z
    .enum([
      'drawdown_limit_reached',
      'insufficient_stake',
      'legacy_backfill',
      'other',
    ])
    .nullable(),
  // When trading on the account started — the promotion date every tenure
  // label counts from; null while `pending` and on an allocation stopped
  // before it ever started.
  started_at: z.string().nullable(),
  // When the executor stopped trading new signals — the elimination moment
  // the auto-close counts from; null unless the allocation was soft-stopped.
  soft_stopped_at: z.string().nullable(),
});
export type Allocation = z.output<typeof AllocationSchema>;
const AllocationsPageSchema = pageSchema(AllocationSchema);

// /v1/stakes row — ONE ROW PER DEPOSIT (a staker can deposit repeatedly;
// distinct-staker counts are a Set over staker_address).
const StakeSchema = z.looseObject({
  stake_id: safeFloat, // the on-chain id — the arg for withdraw/canWithdraw contract calls
  staker_address: AddressSchema,
  provider_address: AddressSchema,
  amount: weiString,
  deposit_timestamp: z.string(),
  // The punished marker: the deadline the contract stamped on the stake when
  // an operator punished its provider, mirrored by the indexer from the
  // PunishmentAppliedRange event and never cleared — the backend's own
  // active/inactive stake totals sum on this field's nullness. Independent
  // axis from the `withdrawn` flag — never conflate the two. The DTO's
  // `inactive` boolean is a dead backend column — never written, false on
  // every punished row (verified live 2026-10-08) — and stays undeclared.
  // unread-field-allow: consumed via @/modules/stake-status — whole Stake rows pass into its structurally-typed params
  punishment_until: z.string().nullable(),
});
export type Stake = z.output<typeof StakeSchema>;
const StakesPageSchema = pageSchema(StakeSchema);

// /v1/stakes/by-user aggregate row. null = no data for that axis; the
// reward fields are USD.
const StakeUserSchema = z.looseObject({
  address: AddressSchema,
  accuracy: ratio01.nullable(),
  mdd: ratio01.nullable(),
  realized_pnl_pct: pct.nullable(),
  stakers_reward_on_user: safeFloat.nullable(),
  stakers_reward_by_user: safeFloat.nullable(),
  // Per-deposit staking earnings, joined by stake_id onto /v1/stakes rows.
  // Includes withdrawn stakes' entries — a reward lookup, never a row source.
  stakes_by_user: z.array(
    z.looseObject({ stake_id: safeFloat, reward: safeFloat.nullable() }),
  ),
});
type StakeUser = z.output<typeof StakeUserSchema>;
const StakeUsersPageSchema = pageSchema(StakeUserSchema);

export function arenaFetch<S extends z.ZodType>(
  path: string,
  schema: S,
): Promise<z.output<S>>;
export function arenaFetch<S extends z.ZodType>(
  path: string,
  schema: S,
  opts: { nullOn404: true },
): Promise<z.output<S> | null>;
export function arenaFetch<S extends z.ZodType>(
  path: string,
  schema: S,
  opts?: { nullOn404?: boolean },
): Promise<z.output<S> | null> {
  return fetchJson({
    backend: 'arena',
    source: 'arenaApi',
    key: path,
    url: `${ARENA_API_BASE}${path}`,
    schema,
    nullOn404: opts?.nullOn404,
  });
}

// Health probe for gateway-outage recovery polling.
export function pingArenaApi(): Promise<boolean> {
  return pingOk(`${ARENA_API_BASE}/v1/ping`);
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

function filterParam(obj: Record<string, unknown>): string {
  return `filter=${encodeURIComponent(JSON.stringify(obj))}`;
}

// Exact-match lookup — the filter is case-sensitive and expects the EIP-55
// checksummed form (both backends ship EIP-55). null = the arena doesn't
// know the address.
async function getIssuerByAddress(address: string): Promise<Issuer | null> {
  const page = await arenaFetch(
    `/v1/issuers?${filterParam({ address })}&limit=1`,
    IssuersPageSchema,
  );
  return page.data[0] ?? null;
}

// Exact-equals username lookup — the SSO-safe identifier (arena's issuer id
// is NOT the SanR issuer id, and the filter grammar 500s on id equality, so
// username is the only cross-backend key; verified live 2026-08-20).
// Case-sensitive; null = the arena doesn't know the username. Bare wallets
// ship username null and can never match — resolve those by address.
async function getIssuerByUsername(username: string): Promise<Issuer | null> {
  const page = await arenaFetch(
    `/v1/issuers?${filterParam({ username: { equals: username } })}&limit=1`,
    IssuersPageSchema,
  );
  return page.data[0] ?? null;
}

// An empty page ends the walk as surely as reaching `total`, so an
// overstated total can't loop forever.
async function fetchAllRows<T>(
  fetchPage: (offset: number) => Promise<Page<T>>,
): Promise<T[]> {
  const rows: T[] = [];
  for (let offset = 0; ; offset += 500) {
    const page = await fetchPage(offset);
    rows.push(...page.data);
    if (page.data.length === 0 || offset + page.data.length >= page.total) {
      break;
    }
  }
  return rows;
}

// All NON-WITHDRAWN stake deposits on one side of the relation, newest-
// first, fetched in 500-row pages until exhausted. Filters withdrawn:false
// alone: punished deposits still hold tokens and must stay visible (marked);
// the active subset (what sums to active_stake_on_user) is the slice with no
// punishment_until, computed by callers. Getting that filter wrong left
// withdrawn rows visible live once.
async function getStakesFiltered(
  filterSide: { provider_address: string } | { staker_address: string },
): Promise<Stake[]> {
  const filter = filterParam({ ...filterSide, withdrawn: false });
  const rows = await fetchAllRows((offset) =>
    arenaFetch(
      `/v1/stakes?${filter}&limit=500&offset=${offset}`,
      StakesPageSchema,
    ),
  );
  return rows.sort(
    (a, b) =>
      new Date(b.deposit_timestamp).getTime() -
      new Date(a.deposit_timestamp).getTime(),
  );
}

const BATCH_LOOKUP_SIZE = 30;

// Batched by-user rows via ?addresses= — comma-separated with the commas
// URL-ENCODED (a raw `,` is a 400), case-insensitive; unknown addresses are
// silently dropped (absent keys). Indexed by LOWERCASED address.
async function getStakeUsersByAddresses(
  addresses: string[],
): Promise<Record<string, StakeUser>> {
  const byAddress: Record<string, StakeUser> = {};
  const pages = await Promise.all(
    chunk(addresses, BATCH_LOOKUP_SIZE).map((batch) =>
      arenaFetch(
        `/v1/stakes/by-user?addresses=${encodeURIComponent(batch.join(','))}&limit=${BATCH_LOOKUP_SIZE}`,
        StakeUsersPageSchema,
      ),
    ),
  );
  for (const page of pages) {
    for (const user of page.data) {
      byAddress[user.address.toLowerCase()] = user;
    }
  }
  return byAddress;
}

// Issuer rows per wallet address, LOWERCASED keys, misses absent.
async function getIssuersByAddresses(
  addresses: string[],
): Promise<Record<string, Issuer>> {
  const byAddress: Record<string, Issuer> = {};
  const pages = await Promise.all(
    chunk(addresses, BATCH_LOOKUP_SIZE).map((batch) =>
      arenaFetch(
        `/v1/issuers?${filterParam({ OR: batch.map((address) => ({ address })) })}&limit=${BATCH_LOOKUP_SIZE}`,
        IssuersPageSchema,
      ),
    ),
  );
  for (const page of pages) {
    for (const issuer of page.data) {
      // Address-filtered rows always carry one; the guard keeps the nullable
      // schema honest without laundering.
      if (issuer.address == null) continue;
      byAddress[issuer.address.toLowerCase()] = issuer;
    }
  }
  return byAddress;
}

const HL = (allocationId: string) =>
  `/v1/allocations/${allocationId}/hyperliquid`;

// The allocation each issuer's trading data reads through, keyed by issuer
// id: the live one (active or soft_stopped) when there is one, else the
// newest stopped one (the request sorts started_at desc). Only that one
// allocation is exposed per issuer — a re-allocated issuer's older windows
// are not rendered. An issuer absent from the map has no trading history:
// render the empty state, call no data endpoint.
async function getAllocationByIssuer(): Promise<Record<string, Allocation>> {
  const rows = await fetchAllRows((offset) =>
    arenaFetch(
      `/v1/allocations?sort=started_at:desc&limit=500&offset=${offset}`,
      AllocationsPageSchema,
    ),
  );
  const byIssuer: Record<string, Allocation> = {};
  for (const row of rows) {
    // A pending allocation is invisible: the issuer stays a candidate and
    // renders no strategy until the executor starts trading.
    if (row.status === 'pending') continue;
    if (byIssuer[row.issuer_id] === undefined || isLive(row)) {
      byIssuer[row.issuer_id] = row;
    }
  }
  return byIssuer;
}

function isLive(allocation: Allocation): boolean {
  return allocation.status !== 'stopped';
}

export type IssuerAllocation = { issuer: Issuer; allocation: Allocation };

// Every issuer whose current allocation the Arena renders — the funded ones
// (a live allocation) and the eliminated ones (a stop with a reason, still
// winding down or closed) — in the issuer list's default order: no sort is
// sent, so the order is backend-owned (one 500-row page, joined
// client-side).
async function getStrategyIssuers(
  resolveAllocations: () => Promise<Record<string, Allocation>>,
): Promise<IssuerAllocation[]> {
  const [page, allocationByIssuer] = await Promise.all([
    arenaFetch('/v1/issuers?limit=500', IssuersPageSchema),
    resolveAllocations(),
  ]);
  return page.data.flatMap((issuer) => {
    const allocation = allocationByIssuer[issuer.id];
    return (
        allocation !== undefined &&
          (isLive(allocation) || eliminationOf(allocation) !== null)
      ) ?
        [{ issuer, allocation }]
      : [];
  });
}

function fundedOf(rows: IssuerAllocation[]): IssuerAllocation[] {
  return rows.filter((row) => isLive(row.allocation));
}

export type AssetAgg = { asset: string; count: number; pnl: number };

export function aggregateByAsset(positions: Position[]): AssetAgg[] {
  const map = new Map<string, AssetAgg>();
  for (const p of positions) {
    const a = map.get(p.asset) ?? { asset: p.asset, count: 0, pnl: 0 };
    a.count += 1;
    a.pnl += p.realized_pnl;
    map.set(p.asset, a);
  }
  return [...map.values()].sort((x, y) => y.count - x.count);
}

// One list query serves every consumer that resolves allocations: the hook
// reads it directly and the strategy-issuers join reads it through the query
// client, so they share one cache entry.
const allocationQuery = queryOptions({
  queryKey: queryKeys.arenaAllocations(),
  queryFn: getAllocationByIssuer,
  staleTime: 60_000,
});

// One query feeds every consumer of the strategy list — the funded view
// selects from the same entry and useArenaEquityCurves chains on it — so
// the issuer page is fetched once.
function strategyIssuersQuery(queryClient: QueryClient) {
  return queryOptions({
    queryKey: queryKeys.arenaStrategyIssuers(),
    queryFn: () =>
      getStrategyIssuers(() => queryClient.fetchQuery(allocationQuery)),
    staleTime: 60_000,
  });
}

export function useArenaStrategyIssuers(): UseQueryResult<IssuerAllocation[]> {
  const queryClient = useQueryClient();
  return useQuery(strategyIssuersQuery(queryClient));
}

// The funded providers alone: every issuer holding a live allocation
// (active or soft_stopped), eliminated or not.
export function useArenaFundedIssuers(): UseQueryResult<IssuerAllocation[]> {
  const queryClient = useQueryClient();
  return useQuery({ ...strategyIssuersQuery(queryClient), select: fundedOf });
}

// `enabled` false (default true) keeps a screen that has nothing to resolve
// yet from fetching the list.
export function useArenaAllocationByIssuer(
  enabled?: boolean,
): UseQueryResult<Record<string, Allocation>> {
  return useQuery({ ...allocationQuery, enabled: enabled !== false });
}

// The wallets holding a live allocation, ascending, derived from the same
// list entry as useArenaAllocationByIssuer: the set a funded listing names
// and a candidate listing excludes. A fetch input for those listings, hence
// also their query-key segment.
export function useArenaFundedAddresses(): UseQueryResult<
  readonly ChecksummedAddress[]
> {
  return useQuery({ ...allocationQuery, select: fundedAddressesOf });
}

function fundedAddressesOf(
  byIssuer: Record<string, Allocation>,
): readonly ChecksummedAddress[] {
  return Object.values(byIssuer)
    .filter(isLive)
    .map((allocation) => allocation.issuer_address)
    .sort();
}

// The profile-context issuer read — keyed by username so walletless SSO
// users resolve too. One query for every screen that reads the row, so a
// page and its header share a cache entry and both cascades (stake and
// prediction) refresh the same copy. `staleTime` (default 60s) lets a
// consumer that only reads the row's stable id keep it never-stale.
export function useArenaIssuer(
  username?: string,
  options?: { staleTime: number },
): UseQueryResult<Issuer | null> {
  const name = username ?? '';
  return useQuery({
    queryKey: queryKeys.arenaIssuer(name),
    queryFn: () => getIssuerByUsername(name),
    enabled: name !== '',
    staleTime: options === undefined ? 60_000 : options.staleTime,
  });
}

// Address-keyed variant for inherently wallet-bound flows (staking targets a
// wallet; bare-wallet issuers have no username to resolve by).
export function useArenaIssuerByAddress(
  address?: string,
): UseQueryResult<Issuer | null> {
  const addr = address ?? '';
  return useQuery({
    queryKey: queryKeys.arenaIssuerByAddress(addr),
    queryFn: () => getIssuerByAddress(addr),
    enabled: addr !== '',
    staleTime: 60_000,
  });
}

export function useArenaMetrics(
  allocationId?: string,
): UseQueryResult<z.output<typeof MetricsSchema>> {
  const id = allocationId ?? '';
  return useQuery({
    queryKey: queryKeys.arenaMetrics(id),
    queryFn: () => arenaFetch(`${HL(id)}/metrics`, MetricsSchema),
    enabled: id !== '',
    staleTime: 60_000,
  });
}

async function getPositionsPage(
  allocationId: string,
): Promise<Page<Position | MalformedRow>> {
  const path = `${HL(allocationId)}/positions?limit=500`;
  const envelope = await arenaFetch(path, PositionsEnvelopeSchema);
  return {
    ...envelope,
    data: parseRowsWithDegrade('arenaApi', path, PositionSchema, envelope.data),
  };
}

export function useArenaPositions(
  allocationId?: string,
): UseQueryResult<Page<Position | MalformedRow>> {
  const id = allocationId ?? '';
  return useQuery({
    queryKey: queryKeys.arenaPositions(id),
    queryFn: () => getPositionsPage(id),
    enabled: id !== '',
    staleTime: 30_000,
  });
}

// Per-asset aggregates, server-computed; requests carry no limit param —
// the endpoint takes none.
export function useArenaMarkets(
  allocationId?: string,
): UseQueryResult<z.output<typeof MarketsSchema>> {
  const id = allocationId ?? '';
  return useQuery({
    queryKey: queryKeys.arenaMarkets(id),
    queryFn: () => arenaFetch(`${HL(id)}/markets`, MarketsSchema),
    enabled: id !== '',
    staleTime: 30_000,
  });
}

// Per-week reward rows — ordering is backend-owned; consumers select by
// is_final, never by array position. The CURRENT week is the
// `is_final: false` row.
export function useArenaWeekly(
  allocationId?: string,
): UseQueryResult<z.output<typeof WeeklySchema>> {
  const id = allocationId ?? '';
  return useQuery({
    queryKey: queryKeys.arenaWeekly(id),
    queryFn: () => arenaFetch(`${HL(id)}/weekly`, WeeklySchema),
    enabled: id !== '',
    staleTime: 60_000,
  });
}

export function useArenaTrades(
  allocationId?: string,
  asset?: string,
): UseQueryResult<Page<Trade>> {
  const id = allocationId ?? '';
  return useQuery({
    queryKey: queryKeys.arenaTrades(id, asset),
    queryFn: () => {
      const q =
        asset != null && asset !== '' ?
          `&asset=${encodeURIComponent(asset)}`
        : '';
      return arenaFetch(`${HL(id)}/trades?limit=500${q}`, TradesPageSchema);
    },
    enabled: id !== '',
    staleTime: 30_000,
  });
}

export function useArenaStakes(
  providerAddress?: string,
): UseQueryResult<Stake[]> {
  const addr = providerAddress ?? '';
  return useQuery({
    queryKey: queryKeys.arenaStakers(addr),
    queryFn: () => getStakesFiltered({ provider_address: addr }),
    enabled: addr !== '',
    staleTime: 60_000,
  });
}

export function useArenaPortfolioStakes(
  stakerAddress?: string,
): UseQueryResult<Stake[]> {
  const addr = stakerAddress ?? '';
  return useQuery({
    queryKey: queryKeys.arenaPortfolioStakes(addr),
    queryFn: () => getStakesFiltered({ staker_address: addr }),
    enabled: addr !== '',
    staleTime: 60_000,
  });
}

// The query key embeds the address list — pass it sorted + deduped or the
// cache fragments.
export function useArenaStakeUsers(
  addresses: string[],
): UseQueryResult<Record<string, StakeUser>> {
  return useQuery({
    queryKey: queryKeys.arenaStakeUsers(addresses),
    queryFn: () => getStakeUsersByAddresses(addresses),
    enabled: addresses.length > 0,
    staleTime: 60_000,
  });
}

// One address's by-user row via the single ?address= alias
// (case-insensitive). null = the address appears in no stake.
export function useArenaStakeUser(
  address?: string,
): UseQueryResult<StakeUser | null> {
  const addr = address ?? '';
  return useQuery({
    queryKey: queryKeys.arenaStakeUser(addr),
    queryFn: async () => {
      const page = await arenaFetch(
        `/v1/stakes/by-user?address=${addr}&limit=1`,
        StakeUsersPageSchema,
      );
      return page.data[0] ?? null;
    },
    enabled: addr !== '',
    staleTime: 60_000,
  });
}

// Per-issuer equity-curve pnl series as one issuer id → pnl[] map, read
// through each issuer's allocation (resolved here — one list query for the
// whole set). Per-allocation query keys so caches survive issuer-set
// changes; issuers without an allocation and curves with ≤1 point are
// absent from the map.
export function useArenaEquityCurveMap(
  issuerIds: string[],
): Record<string, number[]> {
  const { data: allocationByIssuer } = useArenaAllocationByIssuer(
    issuerIds.length > 0,
  );
  const pairs = issuerIds.flatMap((issuerId) => {
    const allocation = allocationByIssuer?.[issuerId];
    return allocation === undefined ?
        []
      : [{ issuerId, allocationId: allocation.id }];
  });
  return useQueries({
    queries: pairs.map(({ allocationId }) => ({
      queryKey: queryKeys.arenaEquityCurve(allocationId),
      queryFn: () =>
        arenaFetch(`${HL(allocationId)}/equity-curve`, HlEquityCurveSchema),
      staleTime: 60_000,
    })),
    combine: (results) => {
      const map: Record<string, number[]> = {};
      pairs.forEach(({ issuerId }, i) => {
        // Stryker disable next-line OptionalChaining: equivalent — useQueries returns exactly one result per query, so results[i] is always defined; the load-bearing ?. is the .data one
        const pnl = results[i]?.data?.pnl;
        if (pnl && pnl.length > 1) map[issuerId] = pnl;
      });
      return map;
    },
  });
}

// Keyed on the list itself so a membership change re-resolves.
export function useArenaIssuersByAddresses(
  addresses: string[],
): UseQueryResult<Record<string, Issuer>> {
  return useQuery({
    queryKey: queryKeys.arenaIssuersByAddresses(addresses),
    queryFn: () => getIssuersByAddresses(addresses),
    enabled: addresses.length > 0,
    staleTime: 60_000,
  });
}

export type EquityCurve = {
  // username falls back to the truncated address for unregistered wallets
  // (then to the arena id when both are null), so legends/tooltips always
  // have a display name.
  issuer: { id: string; username: string; avatar: string | null };
  // time = unix seconds; value = cumulative PnL %; usd = account equity USD.
  // A drifted wire array shorter than time truncates the curve to its
  // complete prefix — never zero-filled: a fabricated 0 reads as a real
  // 0% / $0 point on a money chart.
  points: { time: number; value: number; usd: number }[];
};

async function getEquityCurves(
  rows: IssuerAllocation[],
): Promise<EquityCurve[]> {
  const curves = await Promise.all(
    rows.map(async ({ issuer, allocation }) => {
      const curve = await arenaFetch(
        `${HL(allocation.id)}/equity-curve`,
        HlEquityCurveSchema,
      );
      const points: EquityCurve['points'] = [];
      for (const [i, t] of curve.time.entries()) {
        const pnl = curve.pnl[i];
        const usd = curve.value[i];
        if (pnl === undefined || usd === undefined) break;
        points.push({ time: Math.floor(t / 1000), value: pnl, usd });
      }
      if (points.length === 0) return null;
      return {
        issuer: {
          id: issuer.id,
          username: issuer.username ?? issuer.address?.slice(0, 8) ?? issuer.id,
          avatar: absolutizeAvatarUrl(issuer.avatar),
        },
        points,
      } satisfies EquityCurve;
    }),
  );
  return curves.filter((c): c is EquityCurve => c !== null);
}

// One curve per strategy issuer (funded and eliminated) whose curve is
// non-empty, chained on useArenaStrategyIssuers — the issuer list is fetched
// once for both consumers.
export function useArenaEquityCurves(): UseQueryResult<EquityCurve[]> {
  const { data: rows } = useArenaStrategyIssuers();
  return useQuery({
    queryKey: queryKeys.arenaEquityCurves(
      (rows ?? []).map((row) => row.allocation.id),
    ),
    queryFn: rows ? () => getEquityCurves(rows) : skipToken,
    staleTime: 60_000,
  });
}
